// One-click starter store for a kid.
//
// Picking 12 prizes out of 224 by hand, per child, is the kind of chore that
// stops a parent setting the thing up at all. This proposes a spread and the
// parent edits it down, which is a much easier job than starting from nothing.

export type Difficulty = "gentle" | "balanced" | "ambitious";

export interface SuggestablePrize {
  id: string;
  name: string;
  category: string;
  tier: number;
  eligible_bands: string | null;
  drive_minutes: number | null;
}

// How many prizes to propose at each tier. Tier 1 is about a week of effort,
// tier 4 is a school-year grand prize.
//
// Even "ambitious" keeps two tier-1 items on purpose. The first reward a kid
// actually reaches is what teaches them the loop is real; a store where
// everything is far away reads as "not for me" and they stop looking at it.
// Ambitious makes the middle of the ladder heavier, it does not remove the
// bottom rung.
const QUOTAS: Record<Difficulty, [number, number, number, number]> = {
  gentle: [5, 4, 2, 1],
  balanced: [3, 4, 3, 1],
  ambitious: [2, 3, 4, 2],
};

// House preference, and it matches the research the reward system is built on:
// autonomy and shared time hold their value, objects stop working quickly.
// Ties inside a tier break toward the top of this list.
const CATEGORY_RANK: Record<string, number> = {
  autonomy: 0,
  one_on_one: 1,
  experience: 2,
  adventure: 3,
  food: 4,
  stuff: 5,
  grand: 6,
};

// Nothing to drive to, a local outing, an afternoon, or a whole day out.
function driveBucket(minutes: number | null): number {
  if (minutes == null) return 0;
  if (minutes <= 30) return 1;
  if (minutes <= 75) return 2;
  return 3;
}

function eligibleForBand(prize: SuggestablePrize, band: string): boolean {
  // A null band list means "suits anyone".
  if (!prize.eligible_bands) return true;
  try {
    const bands = JSON.parse(prize.eligible_bands) as unknown;
    if (!Array.isArray(bands) || bands.length === 0) return true;
    return bands.map(String).includes(band);
  } catch {
    // Older rows stored bands as a bare string like "BCD".
    return prize.eligible_bands.includes(band);
  }
}

// Deterministic per (student, prize) so two kids get different-looking stores
// and the same kid gets the same suggestion twice. Avoids Math.random, which
// would make this untestable.
function tieBreaker(studentId: string, prizeId: string): number {
  let hash = 2166136261;
  for (const char of `${studentId}:${prizeId}`) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function suggestStarterSet(
  prizes: SuggestablePrize[],
  options: { studentId: string; band: string; difficulty: Difficulty; excludeIds?: Iterable<string> },
): SuggestablePrize[] {
  const excluded = new Set(options.excludeIds ?? []);
  const quotas = QUOTAS[options.difficulty] ?? QUOTAS.balanced;
  const chosen: SuggestablePrize[] = [];

  for (let tier = 1; tier <= 4; tier += 1) {
    const want = quotas[(tier - 1) as 0 | 1 | 2 | 3];
    if (want <= 0) continue;

    const pool = prizes
      .filter((prize) => prize.tier === tier && !excluded.has(prize.id) && eligibleForBand(prize, options.band))
      .sort((left, right) => {
        const byCategory = (CATEGORY_RANK[left.category] ?? 9) - (CATEGORY_RANK[right.category] ?? 9);
        if (byCategory !== 0) return byCategory;
        // A prize two hours away is a bigger ask of the whole family than one
        // twenty minutes away, so nearer wins. Bucketed rather than exact:
        // 15 vs 30 minutes should not decide anything, and sorting on raw
        // minutes made the order fully determined, which meant every sibling
        // got an identical store and the per-kid tie-break never ran.
        const byDrive = driveBucket(left.drive_minutes) - driveBucket(right.drive_minutes);
        if (byDrive !== 0) return byDrive;
        return tieBreaker(options.studentId, left.id) - tieBreaker(options.studentId, right.id);
      });

    // One pass per category before any category repeats, so a tier never comes
    // back as four variations on snacks.
    const used = new Set<string>();
    const picked: SuggestablePrize[] = [];
    for (let round = 0; picked.length < want && round < 4; round += 1) {
      for (const prize of pool) {
        if (picked.length >= want) break;
        if (picked.includes(prize)) continue;
        const seenThisRound = used.has(`${round}:${prize.category}`);
        if (seenThisRound) continue;
        used.add(`${round}:${prize.category}`);
        picked.push(prize);
      }
    }
    chosen.push(...picked);
  }

  return chosen;
}

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  gentle: "Gentle · more quick wins",
  balanced: "Balanced · a full ladder",
  ambitious: "Ambitious · weighted to bigger goals",
};

export function starterSetShape(difficulty: Difficulty): string {
  const quotas = QUOTAS[difficulty] ?? QUOTAS.balanced;
  return quotas.map((count, index) => `${count} tier ${index + 1}`).join(" · ");
}
